import { routes } from "src/shared/routes";
import { useSSAuth } from "./useSSAuth";
import { useTicketmaster } from "./useTicketmaster";
import { dispatchCustomEvent } from "src/utils/utils";
import { constants } from "src/shared/constants";
import { useTeamConfig } from "../components/TeamConfigProvider";
import { useRouterExt } from "./useRouterExt";
import { useTicketsdotcom } from "./useTicketsdotcom";

export type TLogoutRedirectRoute = (typeof routes)[keyof typeof routes];

export type TLogoutOptions = {
  /**
   * skips DEFAULT_APP_ROUTE_AFTER_LOGOUT and the urlCode append, so the caller
   * keeps full control of the destination. only for redirects that carry
   * required context the team config must not override (e.g. an invite link).
   */
  ignoreTeamConfigRoute?: boolean;
};

export function useLogout() {
  const { logoutFromSs } = useSSAuth();
  const { logoutTicketmaster } = useTicketmaster();
  const { VENDOR, TEAM_GLOBAL_SETTINGS } = useTeamConfig();
  const router = useRouterExt();
  const { handleSignOut } = useTicketsdotcom();
  const urlCode = router?.query?.urlCode;

  /** resolves the single destination every logout path shares */
  const resolveLogoutRoute = (
    redirectRoute?: TLogoutRedirectRoute,
    options?: TLogoutOptions
  ) => {
    if (options?.ignoreTeamConfigRoute) {
      return { pathname: redirectRoute || routes.landing, query: {} };
    }

    // the team config wins so every logout path lands on the same route
    const pathname =
      TEAM_GLOBAL_SETTINGS?.DEFAULT_APP_ROUTE_AFTER_LOGOUT ||
      redirectRoute ||
      routes.landing;

    // preserving the urlCode so the user keeps the same team context
    const query = urlCode && !pathname.includes("?") ? { urlCode } : {};

    return { pathname, query };
  };

  const handleLogout = async (
    redirectRoute?: TLogoutRedirectRoute,
    options?: TLogoutOptions
  ) => {
    const { pathname, query } = resolveLogoutRoute(redirectRoute, options);
    const queryString = new URLSearchParams(query as any).toString();
    const logoutRoute = (queryString ? `${pathname}?${queryString}` : pathname) as TLogoutRedirectRoute; // prettier-ignore

    await logoutFromSs();
    const isSeatGeekOrStripe = VENDOR === constants.vendorUUIDs.seatgeek || VENDOR === constants.vendorUUIDs.stripe; // prettier-ignore

    if (isSeatGeekOrStripe) {
      dispatchCustomEvent(constants.customEvents.userLogout);
      return router.pushSchema({ pathname, query });
    }

    if (VENDOR === constants.vendorUUIDs.ticketsdotcom) {
      dispatchCustomEvent(constants.customEvents.userLogout);
      return await handleSignOut(logoutRoute);
    }

    await logoutTicketmaster(logoutRoute);
    dispatchCustomEvent(constants.customEvents.userLogout);
  };

  return { handleLogout };
}
